import cn from 'classnames';
import { ReactElement, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { HiDownload } from 'react-icons/hi';
import { MdDelete, MdExpandLess, MdExpandMore, MdSave } from 'react-icons/md';
import { NetworkMetadata } from '@blisspointmedia/bpm-types/dist/LogsTracker';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker';
import { Appearance, Design, Intent, Placement, Size } from '@Components/designConstants';
import { EmptyState } from '@Components/EmptyState';
import { Spinner } from '@Components/Spinner';
import { isNotMonday } from '@src/utils/date-utils';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import {
  LOG_TYPE_LABELS,
  LogStatus,
  LogStatusType,
  NetworkEdits,
  STATUS_LABELS,
  STATUS_ORDER
} from './logs-tracker-constants';
import { getNetworkStatus, groupByStatus, matchesSearch } from './logs-tracker-utils';
import { LogsLaneState } from './use-logs-lane';
import { NetworkCard } from './NetworkCard';
import { NetworkEditor } from './NetworkEditor';

interface LogsLaneProps {
  lane: LogsLaneState;
  search: string;
}

export const LogsLane = ({ lane, search }: LogsLaneProps) => {
  const [selectedNetwork, setSelectedNetwork] = useState<string>();

  const allNetworks = Object.values(lane.networkMap ?? {});
  const visibleNetworks = allNetworks
    .filter(network => matchesSearch(network, search))
    .sort((a, b) => a.network.localeCompare(b.network));

  // Prev/next walks the visible networks alphabetically, so the order doesn't jump when an edit
  // moves the open network to another status group.
  const selectedIndex = visibleNetworks.findIndex(({ network }) => network === selectedNetwork);
  const previousNetwork = selectedIndex > 0 ? visibleNetworks[selectedIndex - 1] : undefined;
  const nextNetwork = selectedIndex >= 0 ? visibleNetworks[selectedIndex + 1] : undefined;

  return (
    <div className='logs-lane'>
      <LaneHeader lane={lane} allNetworks={allNetworks} />
      <LaneBody
        isLoading={lane.isLoading}
        hasNetworks={allNetworks.length > 0}
        visibleNetworks={visibleNetworks}
        search={search}
        edits={lane.edits}
        selectedNetwork={selectedNetwork}
        onSelectNetwork={setSelectedNetwork}
      />
      <NetworkEditor
        type={lane.type}
        week={lane.week}
        network={selectedNetwork ? lane.networkMap?.[selectedNetwork] : undefined}
        onEdit={edit => selectedNetwork && lane.editNetwork(selectedNetwork, edit)}
        onPrevious={previousNetwork && (() => setSelectedNetwork(previousNetwork.network))}
        onNext={nextNetwork && (() => setSelectedNetwork(nextNetwork.network))}
        onClose={() => setSelectedNetwork(undefined)}
        hasEdits={lane.hasEdits}
        isSaving={lane.isSaving}
        onSave={lane.saveEdits}
      />
    </div>
  );
};

interface LaneHeaderProps {
  lane: LogsLaneState;
  allNetworks: NetworkMetadata[];
}

const LaneHeader = ({ lane, allNetworks }: LaneHeaderProps) => (
  <div className='lane-header'>
    <div className='lane-title-row'>
      <h2 className='lane-title'>{LOG_TYPE_LABELS[lane.type]}</h2>
      {/* Changing weeks would drop unsaved edits, so the week stays put until they're
          saved or discarded. */}
      <LockedWeekTooltip isLocked={lane.hasEdits}>
        <DatePicker
          isSingle
          fullWeeksOnly
          range={{ start: lane.week, end: lane.week }}
          isDayBlocked={isNotMonday}
          disabled={lane.hasEdits}
          onChange={({ start }) => lane.setWeek(start)}
        />
      </LockedWeekTooltip>
      <Button
        size={Size.SM}
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        leadingElement={<HiDownload />}
        onClick={lane.download}
        disabled={!lane.networkMap}
      >
        CSV
      </Button>
    </div>
    <LaneProgress networks={allNetworks} />
    {lane.hasEdits && (
      <PendingActions
        isSaving={lane.isSaving}
        onDiscard={lane.discardEdits}
        onSave={lane.saveEdits}
      />
    )}
  </div>
);

interface LaneProgressProps {
  networks: NetworkMetadata[];
}

const LaneProgress = ({ networks }: LaneProgressProps) => {
  if (networks.length === 0) {
    return undefined;
  }

  const completedCount = networks.filter(
    network => getNetworkStatus(network) === LogStatus.COMPLETED
  ).length;

  return (
    <div className='lane-summary'>
      <div className='progress-track'>
        <div
          className='progress-fill'
          style={{ width: `${(completedCount / networks.length) * 100}%` }}
        />
      </div>
      <span className='lane-summary-label'>
        {completedCount}/{conditionallyPluralizeWithCount(networks.length, 'network')} complete
      </span>
    </div>
  );
};

interface PendingActionsProps {
  isSaving: boolean;
  onDiscard: () => void;
  onSave: () => void;
}

const PendingActions = ({ isSaving, onDiscard, onSave }: PendingActionsProps) => (
  <div className='lane-pending-actions'>
    <Button
      design={Intent.ERROR}
      leadingElement={<MdDelete />}
      onClick={onDiscard}
      disabled={isSaving}
    >
      Discard Changes
    </Button>
    <Button design={Intent.SUCCESS} leadingElement={<MdSave />} onClick={onSave} loading={isSaving}>
      Save Changes
    </Button>
  </div>
);

interface LockedWeekTooltipProps {
  isLocked: boolean;
  children: ReactElement;
}

const LockedWeekTooltip = ({ isLocked, children }: LockedWeekTooltipProps) => {
  if (!isLocked) {
    return children;
  }

  // The span takes the hover because a disabled button doesn't fire mouse events.
  return (
    <OverlayTrigger
      placement={Placement.BOTTOM}
      overlay={
        <Tooltip id='logs-tracker-week-locked'>
          Save or discard your changes to pick a different week.
        </Tooltip>
      }
    >
      <span className='week-picker-locked'>{children}</span>
    </OverlayTrigger>
  );
};

interface LaneBodyProps {
  isLoading: boolean;
  hasNetworks: boolean;
  visibleNetworks: NetworkMetadata[];
  search: string;
  edits: NetworkEdits;
  selectedNetwork: string | undefined;
  onSelectNetwork: (network: string) => void;
}

const LaneBody = ({
  isLoading,
  hasNetworks,
  visibleNetworks,
  search,
  edits,
  selectedNetwork,
  onSelectNetwork
}: LaneBodyProps) => {
  if (isLoading) {
    return <Spinner />;
  }
  if (!hasNetworks) {
    return <EmptyState title='No networks' subtitle='Nothing is planned for this week.' />;
  }
  if (visibleNetworks.length === 0) {
    return (
      <EmptyState title='No matches' subtitle={`No network or company matches "${search}".`} />
    );
  }

  const groups = groupByStatus(visibleNetworks);
  return (
    <div className='lane-body'>
      {STATUS_ORDER.map(status => (
        <StatusGroup
          key={status}
          status={status}
          networks={groups[status]}
          edits={edits}
          selectedNetwork={selectedNetwork}
          onSelectNetwork={onSelectNetwork}
        />
      ))}
    </div>
  );
};

interface StatusGroupProps {
  status: LogStatusType;
  networks: NetworkMetadata[];
  edits: NetworkEdits;
  selectedNetwork: string | undefined;
  onSelectNetwork: (network: string) => void;
}

const StatusGroup = ({
  status,
  networks,
  edits,
  selectedNetwork,
  onSelectNetwork
}: StatusGroupProps) => {
  // Completed starts collapsed so the networks that still need work stay on screen.
  const [isExpanded, setIsExpanded] = useState(status !== LogStatus.COMPLETED);

  return (
    <section className={cn('status-group', status)}>
      <Button
        className='status-group-header'
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        size={Size.SM}
        aria-expanded={isExpanded}
        leadingElement={isExpanded ? <MdExpandLess /> : <MdExpandMore />}
        trailingElement={<span className='status-group-count'>{networks.length}</span>}
        onClick={() => setIsExpanded(current => !current)}
      >
        {STATUS_LABELS[status]}
      </Button>
      {isExpanded && (
        <div className='status-group-cards'>
          {networks.map(network => (
            <NetworkCard
              key={network.network}
              network={network}
              status={status}
              isEdited={network.network in edits}
              isSelected={network.network === selectedNetwork}
              onClick={() => onSelectNetwork(network.network)}
            />
          ))}
        </div>
      )}
    </section>
  );
};
